import {
  DataType,
  Form,
  FormOptions,
  FormSkeleton,
  Section,
  SelectOption,
  useNotify
} from '@globaleris/ui.library'
import { Button, Grid, Paper } from '@material-ui/core'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useHistory } from 'react-router'
import { SettlementGroupTable } from '../../../api/Argentia/endpoints/settlementGroups/model/settlementGroups'
import { EnumFormaPago } from '../../../api/Argentia/enums'
import { EnumTypeSettlementGroup } from '../../../api/Argentia/enums/typeSettlementGroup'
import { PaymentMethodTypeModel } from '../../../api/Argentia/model/contracts'
import { EnumTipoMetodoPago } from '../../../api/oData/enums/enumTipoMetodoPago'
import Page from '../../../components/page/page'
import PageHeader from '../../../components/page/pageHeader'
import { enumToSelectOptionArray } from '../../../helpers'
import { missingFieldsForm } from '../../../helpers/validateFormHelper'
import { useConfirmationDialog } from '../../../hooks'
import { NonAuthRoutes } from '../../../model'
import { ConfirmationDialogs } from '../../../model/enums/confirmationDialogs'
import { getCustomerDomain } from '../../../redux'
import { checkValidIBAN } from '../../contracts/contracts.service'
import {
  mapSettlementGroupEditToBatchRequest,
  mapSettlementGroupToEditFormData,
  paymentMethodTypesToOptionsMapper
} from '../settlementGroups.mapper'
import {
  RulebookSettlementGroupAggregatedEditValidations,
  SettlementGroupAggregatedEditFormDataSections,
  SettlementGroupAggregatedEditFormIsDirty,
  SettlementGroupAggregatedEditFormValidations
} from '../settlementGroups.model'
import {
  fetchPaymentMethodsTypes,
  fetchPostalCodes,
  getPostalAddressByID,
  isSettlementGroupCodeDuplicated,
  updateSettlementGroupBatch
} from '../settlementGroups.service'

export interface SettlementGroupAggregatedEditFormDataType {
  //header section
  id: number | undefined
  code: string | undefined
  description: string | undefined
  typeSettlementGroup: EnumTypeSettlementGroup | undefined
  subjectId: number | undefined
  subject: string | undefined

  //payment section
  paymentMethodId?: number | undefined
  paymentMethod?: EnumTipoMetodoPago | undefined
  iban?: string | undefined

  //Billing address section
  addressId?: number | undefined
  street?: string | undefined
  postalCodeSearch?: SelectOption | undefined | null
  postalCode?: string | undefined | null
  municipality?: string | undefined | null
  province?: string | undefined | null
  country?: string | undefined | null
  municipalityId?: number | undefined
  provinceId?: number | undefined
  countryId?: number | undefined
}

const INTIAL_VALIDATIONS: SettlementGroupAggregatedEditFormValidations = {
  all: true,
  iban: true,
  postalCode: true
}

const INTIAL_FORM_IS_DIRTY: SettlementGroupAggregatedEditFormIsDirty = {
  all: false,
  header: false,
  payment: false,
  billingAddress: false
}

export interface SettlementGroupAggregatedEditPageProps {
  location: {
    state: {
      settlementGroup: SettlementGroupTable
      settlementGroupCodes?: { id: number; code: string }[]
    }
  }
}

export const SettlementGroupAggregatedEditPage: React.FC<
  SettlementGroupAggregatedEditPageProps
> = props => {
  const [t] = useTranslation(['globalerisLibrary', 'settlementGroups', 'enums'])

  const [formData, setFormData] = useState(
    mapSettlementGroupToEditFormData(props.location.state.settlementGroup)
  )
  const [paymentMethodOptions, setPaymentMethodOptions] = useState<PaymentMethodTypeModel[]>([])
  const [formIsDirty, setFormIsDirty] =
    useState<SettlementGroupAggregatedEditFormIsDirty>(INTIAL_FORM_IS_DIRTY)
  const [formEditValidations, setFormEditValidations] =
    useState<SettlementGroupAggregatedEditFormValidations>(INTIAL_VALIDATIONS)

  const [formIsLoading, setFormIsLoading] = useState<boolean>(true)
  const [isFetching, setIsFetching] = useState<boolean>(false)
  const [requiredText, setRequiredText] = useState<boolean>(false)
  const [ibanRequired, setIbanRequired] = useState<boolean>(false)
  // (props.location.state.settlementGroup.paymentMethod && props.location.state.settlementGroup.paymentMethod. === EnumFormaPago.Domiciliacion ? true : false)
  const [ibanIsValid, setIbanIsValid] = useState<boolean | undefined>(true)

  // const [validations, setValidations] = useState<ContractEditFormValidations>(initialValidations)

  const { getInformation, getConfirmation, closeConfirmation, closeInformation } =
    useConfirmationDialog()
  const history = useHistory()
  const { info, error } = useNotify()

  // Handlers Starts

  const handleFormSave = async () => {
    if (formData) {
      if (!missingFieldsForm(formEditSection, formData) && !requiredTextMissing(formData)) {
        if (
          isSettlementGroupCodeDuplicated(
            formData.code,
            props.location.state.settlementGroupCodes ?? [],
            formData.id
          )
        ) {
          error(t('settlementGroups:notifications.create.duplicatedCode', { code: formData.code }))
          return true
        }
        setIsFetching(true)
        await updateSettlementGroupBatch(
          mapSettlementGroupEditToBatchRequest(formData, formIsDirty)
        ).then(response => {
          if (response.success) {
            info(t('settlementGroups:forms.edit.save.success'))
            history.push(NonAuthRoutes.SettlementGroups)
          } else {
            error(t('settlementGroups:forms.edit.save.error'))
          }
          setIsFetching(false)
        })
      } else {
        error(t('settlementGroups:forms.add.save.incompleteForm'))
      }
      return true
    } else {
      history.goBack()
      return false
    }
  }

  const handleCancelClick = () => {
    if (formIsDirty.all) {
      getConfirmation(ConfirmationDialogs.CANCEL_PENDING_CHANGES).then(async () => {
        closeConfirmation()
        history.goBack()
      })
    } else {
      history.goBack()
    }
  }

  // Handlers Ends

  // Form Definition Starts

  const options: FormOptions = {
    elevation: 1,
    columns: 4,
    analytics: {
      customer: getCustomerDomain(),
      view: t('settlementGroups:forms.edit.analytics')
    }
  }

  const formEditSection: Section[] = [
    {
      title: t('settlementGroups:forms.add.title.settlementGroup'),
      inputs: [
        {
          name: 'code',
          label: t('settlementGroups:forms.add.inputs.code'),
          type: DataType.TEXT,
          required: true,
          onBlur: value => {
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.header)
          }
        },
        {
          name: 'description',
          label: t('settlementGroups:forms.add.inputs.description'),
          type: DataType.TEXT,
          required: true,
          onBlur: value => {
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.header)
          }
        },
        {
          name: 'typeSettlementGroup',
          label: t('settlementGroups:forms.add.inputs.typeSettlementGroup'),
          type: DataType.SELECT,
          selectOptions: enumToSelectOptionArray(
            EnumTypeSettlementGroup,
            'enums:EnumTypeSettlementGroup'
          ).filter(
            e =>
              e.value === EnumTypeSettlementGroup.FacturacionAgregada ||
              e.value === EnumTypeSettlementGroup.FacturacionResumen
          ),
          required: true,
          onBlur: value => {
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.header)
          }
        },
        {
          name: 'subject',
          label: t('settlementGroups:forms.add.inputs.subject'),
          type: DataType.TEXT,
          readOnly: true
        }
      ]
    },
    {
      title: t('settlementGroups:forms.add.title.paymentMethod'),
      inputs: [
        {
          name: 'paymentMethod',
          label: t('settlementGroups:forms.add.inputs.paymentMethod'),
          type: DataType.SELECT,
          selectOptions: paymentMethodTypesToOptionsMapper(paymentMethodOptions),
          clearable: true,
          required: true,
          onChange: paymentMethodSelected => {
            handleIbanRequired(+paymentMethodSelected)
            if (formData.iban !== undefined && formData.iban !== '') {
              //Force iban re-check if it is not empty
              checkIban(formData.iban)
            }
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.payment)
          }
        },
        {
          name: 'iban',
          label: t('settlementGroups:forms.add.inputs.iban'),
          type: DataType.TEXT,
          onBlur: iban => {
            handleNewIban(`${iban}`)
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.payment)
          },
          required: ibanRequired,
          clearable: true
        }
      ]
    },
    {
      title: t('settlementGroups:forms.add.title.billingDirection'),
      inputs: [
        {
          name: 'postalCodeSearch',
          label: t('settlementGroups:forms.add.inputs.postalCode'),
          type: DataType.SEARCHFIELD,
          apiCall: fetchPostalCodes,
          maxLength: 5,
          required: true,
          clearable: true,
          onChange: async apiCallObject => {
            ;(await updateAddress(apiCallObject))
              ? validateField(RulebookSettlementGroupAggregatedEditValidations.POSTAL_CODE, true)
              : validateField(RulebookSettlementGroupAggregatedEditValidations.POSTAL_CODE, false)
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.billingAddress)
          }
        },
        {
          name: 'municipality',
          label: t('settlementGroups:forms.add.inputs.municipality'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'province',
          label: t('settlementGroups:forms.add.inputs.province'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'country',
          label: t('settlementGroups:forms.add.inputs.country'),
          type: DataType.TEXT,
          required: true,
          readOnly: true
        },
        {
          name: 'street',
          label: t('settlementGroups:forms.add.inputs.street'),
          type: DataType.TEXT,
          required: true,
          onBlur: value => {
            handleFormIsDirty(SettlementGroupAggregatedEditFormDataSections.billingAddress)
          }
        }
        // {
        //   name: 'useExistingAccount',
        //   label: t('contracts:forms.add.inputs.useExistingAccount'),
        //   type: DataType.SWITCH,
        //   readOnly: !clientData?.id,
        //   onChange: value => {
        //     if (value) {
        //       handleShowExistingAccounts()
        //     }
        //   }
        // }
      ]
    }
  ]
  // Form Definition Ends

  // DataTable Config Starts
  // DataTable Config Ends

  // Functions Starts
  const updateAddress = async (apiCallObject: any): Promise<boolean> => {
    if (apiCallObject) {
      const postalAddress = await getPostalAddressByID(+apiCallObject.value).catch(e => {})
      if (postalAddress) {
        //update Form State
        setFormData(prevState => ({
          ...prevState,
          // street: undefined,
          municipality: postalAddress.municipality.description,
          province: postalAddress.province.description,
          country: postalAddress.country.description,
          municipalityId: postalAddress.municipality.id,
          provinceId: postalAddress.province.id,
          countryId: postalAddress.country.id,
          postalCode: postalAddress.postalCode
        }))
        return true
      }
    } else {
      // if field is NULL, then clean dependant Form fields
      setFormData(prevState => ({
        ...prevState,
        // street: undefined,
        municipality: undefined,
        province: undefined,
        country: undefined,
        municipalityId: undefined,
        provinceId: undefined,
        countryId: undefined,
        postalCode: undefined
      }))
    }
    return false
  }

  const requiredTextMissing = (formData: SettlementGroupAggregatedEditFormDataType): boolean => {
    if (formData.code === '' || formData.description === '') return true
    else if (formData.iban === '' && ibanRequired) return true
    else return false
  }

  const validateField = (
    validation: RulebookSettlementGroupAggregatedEditValidations,
    isValid: boolean
  ) => {
    setFormEditValidations(prevState => {
      const newValidation = { ...prevState, [validation]: isValid }
      const formIsValid = newValidation.iban && newValidation.postalCode
      return { ...newValidation, all: formIsValid }
    })
  }

  const handleFormIsDirty = (formSection: SettlementGroupAggregatedEditFormDataSections) => {
    const dirtySectionName = SettlementGroupAggregatedEditFormDataSections[formSection]
    setFormIsDirty(prevState => ({
      ...prevState,
      [dirtySectionName]: true,
      all: true
    }))
  }

  const handleNewIban = (iban: string) => {
    if (iban === '' && !ibanRequired) {
      validateField(RulebookSettlementGroupAggregatedEditValidations.IBAN, true)
      return
    }
    checkIban(iban)
  }

  const checkIban = async (iban: string) => {
    setIsFetching(true)
    const response = await checkValidIBAN(iban)
    if (response.success && response.data) {
      if (response.data.isValid) {
        setIbanIsValid(true)
        validateField(RulebookSettlementGroupAggregatedEditValidations.IBAN, true)
      } else {
        error(t('settlementGroups:forms.add.validations.ibanError'))
        setIbanIsValid(false)
        validateField(RulebookSettlementGroupAggregatedEditValidations.IBAN, false)
      }
      setIsFetching(false)
    } else {
      error(t('settlementGroups:forms.add.validations.ibanFetch'))
      setIbanIsValid(undefined)
      validateField(RulebookSettlementGroupAggregatedEditValidations.IBAN, false)
      setIsFetching(false)
    }
  }
  const handleIbanRequired = (paymentMethodSelected: number) => {
    const paymentMethod = paymentMethodOptions.find(
      paymentMethod => paymentMethod.Id === paymentMethodSelected
    )
    if (paymentMethod && paymentMethod.methodType === EnumFormaPago.Domiciliacion) {
      setIbanRequired(true)
    } else {
      ibanRequired && setIbanRequired(false)
      validateField(RulebookSettlementGroupAggregatedEditValidations.IBAN, true)
    }
  }

  // Functions Ends

  // Hooks Starts
  useEffect(() => {
    const getPaymentMethods = async () => {
      setFormIsLoading(true)
      await fetchPaymentMethodsTypes().then(response => {
        if (response.success && response.data !== undefined) {
          setPaymentMethodOptions(response.data)
          setFormIsLoading(false)
        } else {
          error(t('settlementGroups:forms.add.validations.paymentMethodFetchError'))
        }
      })
    }
    getPaymentMethods()
  }, [])

  useEffect(() => {
    // initial ibanRequired State
    if (formData.paymentMethod && paymentMethodOptions) handleIbanRequired(formData.paymentMethod)
  }, [paymentMethodOptions])
  // Hooks Ends

  return (
    <>
      <Page>
        <PageHeader title={t('settlementGroups:forms.edit.title')}>
          <Button
            variant="text"
            color="primary"
            size="small"
            disabled={formIsLoading || isFetching}
            onClick={handleCancelClick}
          >
            {t('settlementGroups:forms.add.buttons.cancel')}
          </Button>
          <Button
            variant="contained"
            color="primary"
            size="small"
            disabled={formIsLoading || isFetching || !formIsDirty.all || !formEditValidations.all}
            // hasPermission={hasPermission(Resources.Client, [UserPermission.EDIT])}
            onClick={handleFormSave}
          >
            {t('settlementGroups:forms.add.buttons.save')}
          </Button>
        </PageHeader>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <Paper elevation={6}>
              {formIsLoading ? (
                <>
                  <FormSkeleton columns={3} inputs={6} elevation={2} withTitle={true} />
                  <FormSkeleton columns={3} inputs={9} elevation={2} withTitle={true} />
                </>
              ) : (
                <Form
                  title={t('settlementGroups:forms.edit.formTitle')}
                  sections={formEditSection}
                  data={formData}
                  options={options}
                />
              )}
            </Paper>
          </Grid>
        </Grid>
      </Page>
    </>
  )
}
